<script setup>
import { showModal } from '@/Utils/modalBus'
import { ref, computed, onMounted } from 'vue';
import _ from 'lodash';
import Spinner from "@/components/ui/Spinner.vue";
import PaywallService from '@/services/PaywallService';
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import { useNotification } from "@kyvg/vue3-notification";
import FormButton from '@/components/Atoms/FormButton.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import Alert from '@/components/Atoms/Alert.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconBin from '@/components/icon/IconBin.vue';
import IconSearch from '@/components/icon/IconSearch.vue';
import IconCross from '@/components/icon/IconCross.vue';
import IconEye from '@/components/icon/IconEye.vue';
import IconEyeCrossed from '@/components/icon/IconEyeCrossed.vue';
import IconDownload from '@/components/icon/IconDownload.vue';
import Tooltip from '@/components/Atoms/Tooltip.vue';
import ButtonCopyToClipboard from '@/components/Moleculas/ButtonCopyToClipboard.vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';

const { notify } = useNotification()
const store = useStore()
const router = useRouter()
const route = useRoute()

const props = defineProps({
	paywallStreamId: {
		type: Number,
		require: false
	}
})

// Email access is being retired in favour of Complimentary promo codes. Send
// admins to the Promo tab using the same router-query tab mechanism the
// PaywallManage tab bar uses (route.query.paywallTab).
const goToPromo = () => {
	if (route.query.paywallTab !== 'promo') {
		router.replace({ query: { ...route.query, paywallTab: 'promo' } }).catch(() => { })
	}
}

// Refs
const users = ref([])
const loading = ref(false)
const selected = ref(null)
const search = ref('')
const revealedIds = ref([])

// Computed
const currentPaywall = computed(() => store.getters['Paywalls/currentPaywall'])

const filteredUsers = computed(() => {
	const query = search.value.trim().toLowerCase()
	if (!query) return users.value
	return users.value.filter((u) =>
		String(u.email || '').toLowerCase().includes(query)
	)
})

const hasUsers = computed(() => users.value.length > 0)

// Subtle zebra striping for easier row scanning
const zebraBg = (idx) => idx % 2 === 0 ? 'bg-surface-2/60' : 'bg-surface-2/25'

// Lifecycle
onMounted(async () => {
	await setupUsers();
})

// Methods
const requestDelete = (item) => {
	selected.value = item
	showModal("remove-email-access");
}

const isRevealed = (id) => revealedIds.value.includes(id)

const toggleReveal = (id) => {
	if (isRevealed(id)) {
		revealedIds.value = revealedIds.value.filter((x) => x !== id)
		return
	}
	revealedIds.value = [...revealedIds.value, id]
	// Auto-hide after a short window so passwords aren't left exposed.
	setTimeout(() => {
		revealedIds.value = revealedIds.value.filter((x) => x !== id)
	}, 8000)
}

const clearSearch = () => { search.value = '' }

const onEmailAccessRemoveConfirm = async () => {
	try {
		const id = selected.value.id
		const email = selected.value.email

		await PaywallService.removePaywallEmailAccess(props.paywallStreamId, id, email)

		const userIndex = users.value.findIndex(obj => Number(obj.id) === Number(id))
		users.value.splice(userIndex, 1);

		notify({ group: "success", text: "Email access removed" })
		selected.value = null
	} catch (err) {
		console.error("paywall remove access email failed", err)
		notify({ group: "error", text: "Couldn't remove email access" })
	}
}

const setupUsers = async () => {
	loading.value = true;
	try {
		const usersData = await PaywallService.getUsers();
		users.value = _.orderBy(_.filter(usersData, (user) => {
			return user.plain_pass && user.paywall === props.paywallStreamId
		}), ['id'], ['desc'])
	} catch (err) {
		notify({ group: 'error', text: err.message || "Couldn't load email access list" });
		console.error('getUsers failed', err);
	}
	loading.value = false;
}

const csvEscape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`

const onBtnExport = () => {
	if (!filteredUsers.value.length) return
	const header = ['Email', 'Password']
	const rows = filteredUsers.value.map((u) => [u.email, u.plain_pass])
	const csv = [header, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n')
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = `${currentPaywall.value?.title || 'paywall'}-email-access.csv`
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}

defineExpose({ setupUsers })
</script>

<template>
	<section class="bg-surface-1 rounded-xl p-4 sm:p-5">
		<!-- Loading -->
		<div v-if="loading" class="flex items-center justify-center min-h-[160px]">
			<Spinner spinner-color="var(--c-dark-8)" />
		</div>

		<template v-else>
			<!-- Header (only when existing grants remain to manage) -->
			<div v-if="hasUsers" class="flex flex-col sm:flex-row sm:items-start gap-3 mb-4">
				<div class="min-w-0 grow">
					<h3 class="text-sm font-semibold text-white mb-0.5">Email access</h3>
					<p class="text-tiny text-surface-7 mb-0">These viewers still have free access to this paywall. Email
						access is being retired — you can revoke existing entries here, but new ones are no longer added.</p>
				</div>
				<div class="flex items-center gap-2 shrink-0">
					<FormButton type="secondary" size="sm" :disabled="!filteredUsers.length" @click="onBtnExport">
						<icon-base fill="none" class="!mr-1.5">
							<icon-download />
						</icon-base>
						Export CSV
					</FormButton>
				</div>
			</div>

			<!-- Deprecation notice: sole content when empty, above the list otherwise -->
			<Alert type="warning2" hideIcon :class="{ 'mb-4': hasUsers }">
				<div class="flex flex-col gap-2.5">
					<div>
						<p class="text-sm font-semibold text-white mb-0.5">Email access is being retired</p>
						<p class="text-tiny text-white/80 mb-0">To give one person free access, create a Complimentary promo
							code limited to a single redemption instead.</p>
					</div>
					<ol class="text-tiny text-white/80 list-decimal pl-4 flex flex-col gap-0.5 mb-0">
						<li>Open Promotional codes</li>
						<li>Create a Complimentary (100% off) code</li>
						<li>Set max redemptions to 1 and share it with that viewer</li>
					</ol>
					<div>
						<FormButton size="sm" @click="goToPromo">Go to Promotional codes</FormButton>
					</div>
				</div>
			</Alert>

			<!-- Existing grants: read-only management (search, view, copy, delete) -->
			<template v-if="hasUsers">
			<!-- Search -->
			<div class="flex items-center mb-3 relative max-w-xs">
				<icon-base class="absolute z-10 left-2 text-surface-7">
					<icon-search />
				</icon-base>
				<FormInput placeholder="Search by email" customClasses="!px-8" v-model="search" />
				<FormButton class="absolute z-10 right-0" isIcon type="link" v-if="search" @click="clearSearch">
					<icon-base>
						<icon-cross />
					</icon-base>
				</FormButton>
			</div>

			<div class="overflow-x-auto">
				<table class="w-full min-w-[620px] border-separate border-spacing-y-1 text-left">
					<thead>
						<tr class="text-[11px] uppercase tracking-wider text-surface-6">
							<th class="font-semibold px-4 pb-1 w-12">#</th>
							<th class="font-semibold px-4 pb-1">Email</th>
							<th class="font-semibold px-4 pb-1 w-[38%]">Password</th>
							<th class="font-semibold px-4 pb-1 w-16 text-right">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-if="!filteredUsers.length">
							<td colspan="4" class="px-4 py-8 text-center text-tiny text-surface-6">
								No results for “{{ search }}”.
							</td>
						</tr>
						<tr v-for="(user, idx) in filteredUsers" :key="user.id" class="group">
							<!-- # -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors rounded-l-lg"
								:class="zebraBg(idx)">
								<span class="text-tiny text-surface-6 tabular-nums">{{ idx + 1 }}</span>
							</td>
							<!-- Email -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors" :class="zebraBg(idx)">
								<div class="flex items-center gap-1.5 min-w-0">
									<span class="text-sm text-surface-text-1 truncate" :title="user.email">{{ user.email }}</span>
									<ButtonCopyToClipboard class="shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
										:text="user.email" />
								</div>
							</td>
							<!-- Password -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors" :class="zebraBg(idx)">
								<div class="flex items-center gap-1.5 min-w-0">
									<span class="font-mono text-sm text-surface-8 truncate select-all">
										{{ isRevealed(user.id) ? user.plain_pass : '••••••••••' }}
									</span>
									<Tooltip wrapperClass="leading-none shrink-0 ml-auto"
										:content="isRevealed(user.id) ? 'Hide' : 'Reveal'">
										<FormButton type="link" isIcon size="sm" @click="toggleReveal(user.id)">
											<icon-base fill="transparent">
												<icon-eye v-if="!isRevealed(user.id)" />
												<icon-eye-crossed v-else />
											</icon-base>
										</FormButton>
									</Tooltip>
									<ButtonCopyToClipboard class="shrink-0" :text="user.plain_pass" />
								</div>
							</td>
							<!-- Actions -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors rounded-r-lg"
								:class="zebraBg(idx)">
								<div class="flex items-center justify-end">
									<Tooltip wrapperClass="leading-none" content="Remove access">
										<FormButton type="secondary" isIcon size="sm" class="hover:!text-danger"
											@click="requestDelete(user)">
											<icon-base fill="transparent">
												<icon-bin />
											</icon-base>
										</FormButton>
									</Tooltip>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<p class="text-tiny text-surface-6 mb-0 mt-2">
				{{ filteredUsers.length }} {{ filteredUsers.length === 1 ? 'person has' : 'people have' }} free access.
			</p>
			</template>
		</template>

		<confirm-modal modalId="remove-email-access" message="Remove email access?"
			body="This person will lose free access and will need to pay to watch. This can't be undone."
			okText="Remove" cancelText="Cancel" modal-type="danger" @modal-confirm="onEmailAccessRemoveConfirm" />
	</section>
</template>

<style scoped></style>
